import { useSelectedMembersToAdd } from "@circle-react/components/Modals/AddSpaceMember/hooks/useSelectedMembersToAdd";
import { MembersToAddList } from "@circle-react/components/Modals/AddSpaceMember/MembersToAddList";
import { validateInviteMembersQueryParams } from "@circle-react/components/SettingsApp/ManageMembers/InviteMembers/queryParamsSchema";
import { CommunityMemberSelectCompass } from "@circle-react/components/shared/CommunityMemberSelectCompass";
import { SwitchField } from "@circle-react/components/SpaceGroups/NewFormV3/SwitchField";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { Badge } from "@circleco/compass/components/Badge";
import { Divider } from "@circleco/compass/components/Divider";
import { Typography } from "@circleco/compass/components/Typography";
import { Link, useLocation } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { t } from "@/i18n-js/instance";

import type { AddSpaceGroupMemberContentCompassFormValues } from "./types";

const communityMemberSelectName = "community_member_ids";
const addAllMembersFieldName = "add_all_members";

export interface AddMemberContentCompassProps {
  onHasMembersAddedChange?: (hasMembersAdded: boolean) => void;
}

export const AddMemberContentCompass = ({
  onHasMembersAddedChange,
}: AddMemberContentCompassProps) => {
  const { watch, setValue } =
    useFormContext<AddSpaceGroupMemberContentCompassFormValues>();
  const { pathname } = useLocation();

  const watchedMemberIds = watch(communityMemberSelectName, []);
  const memberIds = useMemo(() => watchedMemberIds ?? [], [watchedMemberIds]);
  const memberSearchFilters = useMemo(
    () => ({ exclude_ids: memberIds }),
    [memberIds],
  );
  const shouldAddEveryone = watch(addAllMembersFieldName, false);
  const hasMembers = memberIds.length > 0;
  const { membersToAdd, handleSelectMember, removeMemberId } =
    useSelectedMembersToAdd({
      memberIds,
      onMemberIdsChange: nextValue =>
        setValue(communityMemberSelectName, nextValue),
    });

  const handleMemberChange = (memberId: number | string | null) => {
    if (memberId == null) return;

    const normalizedMemberId = String(memberId);
    if (!memberIds.includes(normalizedMemberId)) {
      setValue(communityMemberSelectName, [normalizedMemberId, ...memberIds]);
    }
  };

  const hasMembersAdded = hasMembers || shouldAddEveryone;

  useEffect(() => {
    onHasMembersAddedChange?.(hasMembersAdded);
  }, [hasMembersAdded, onHasMembersAddedChange]);

  return (
    <div className="flex flex-col gap-6">
      <Typography variant="body-md" color="primary">
        {t("space_groups.add_members_to_space_group_description_without_link")}{" "}
        <Link
          className="text-primary underline"
          to={settingsNavPathV3.members.invite}
          search={validateInviteMembersQueryParams({ from: pathname })}
        >
          {t("space_groups.add_members_to_space_group_link")}
        </Link>
        .
      </Typography>
      <CommunityMemberSelectCompass
        disabled={shouldAddEveryone}
        value={null}
        onChange={handleMemberChange}
        onSelectMember={handleSelectMember}
        filters={memberSearchFilters}
        searchMode="contains"
        placeholder={t("community_member_select.search_placeholder")}
        initialMessage={t("community_member_select.start_typing_a_name")}
      />
      <MembersToAddList
        memberIds={memberIds}
        members={membersToAdd}
        onRemove={removeMemberId}
      />
      {!hasMembers && (
        <>
          <div className="flex w-full items-center gap-2">
            <Divider className="flex-1" />
            <Badge variant="secondary" label={t("or")} />
            <Divider className="flex-1" />
          </div>
          <SwitchField
            name={addAllMembersFieldName}
            label={t("space_groups.form.add_everyone_from_community")}
          />
        </>
      )}
    </div>
  );
};
